/**
 * 达人订单详情页 - 底部操作按钮栏
 */

import type { AfterSaleState } from "../hooks";

interface ActionBarProps {
  orderStatus: number;
  afterSaleState: AfterSaleState;
  hasCommented: boolean;
  onAction: (action: string) => void;
}

export default function ActionBar({ orderStatus, afterSaleState, hasCommented, onAction }: ActionBarProps) {
  return (
    <div
      className="fixed bottom-0 left-0 right-0 bg-white px-4 z-40 flex gap-3"
      style={{ borderTop: "1px solid #F5F5F5", paddingTop: "8px", paddingBottom: "calc(8px + env(safe-area-inset-bottom, 0px))" }}
    >
      {/* 待支付：支付订单 + 取消订单 + 联系客服 */}
      {orderStatus === 0 && (
        <>
          <ActionButton
            onClick={() => onAction("pay")}
            text="支付订单"
            styleType="primary"
          />
          <ActionButton
            onClick={() => onAction("cancel")}
            text="取消订单"
            styleType="danger-outline"
          />
          <ActionButton
            onClick={() => onAction("service")}
            text="联系客服"
            styleType="info"
          />
        </>
      )}

      {/* 已取消(-1)：再次下单 + 联系客服 */}
      {orderStatus === -1 && (
        <>
          <ActionButton
            onClick={() => onAction("reorder")}
            text="再次下单"
            styleType="primary"
          />
          <ActionButton
            onClick={() => onAction("service")}
            text="联系客服"
            styleType="info"
          />
        </>
      )}

      {/* 退款申请中(-10)：仅联系客服 */}
      {orderStatus === -10 && (
        <ActionButton
          onClick={() => onAction("service")}
          text="联系客服"
          styleType="info"
        />
      )}

      {/* 待商家确认/合同签约/待出行/待服务：申请售后 + 联系客服 */}
      {[10, 13, 16, 20, 30].includes(orderStatus) && (
        <>
          <ActionButton
            onClick={() => onAction("refund")}
            text="申请售后"
            styleType="warning"
          />
          <ActionButton
            onClick={() => onAction("service")}
            text="联系客服"
            styleType="info"
          />
        </>
      )}

      {/* 待评价(40)：评价 + 申请售后 + 联系客服 */}
      {orderStatus === 40 && (
        <>
          <ActionButton
            onClick={() => onAction("comment")}
            text="去评价"
            styleType="comment"
          />
          <ActionButton
            onClick={() => onAction("refund")}
            text="申请售后"
            styleType="warning"
          />
          <ActionButton
            onClick={() => onAction("service")}
            text="联系客服"
            styleType="info"
          />
        </>
      )}

      {/* 已完成(50)：申请售后/开票(互斥) + 联系客服 */}
      {orderStatus === 50 && (
        <>
          {afterSaleState === "none" && (
            <>
              <ActionButton
                onClick={() => onAction("refund")}
                text="申请售后"
                styleType="warning"
              />
              <ActionButton
                onClick={() => onAction("invoice")}
                text="申请开票"
                styleType="invoice"
              />
              <ActionButton
                onClick={() => onAction("service")}
                text="联系客服"
                styleType="info"
              />
            </>
          )}
          {afterSaleState !== "none" && (
            <button
              onClick={() => onAction("service")}
              className="w-full py-2.5 rounded-full text-sm font-medium cursor-pointer text-white"
              style={{ background: "#6366F1" }}
            >
              联系客服
            </button>
          )}
        </>
      )}

      {/* 已退款(-20)：仅联系客服 */}
      {orderStatus === -20 && (
        <ActionButton
          onClick={() => onAction("service")}
          text="联系客服"
          styleType="info"
        />
      )}
    </div>
  );
}

/** 按钮子组件 */
type ButtonStyle = "primary" | "danger-outline" | "info" | "warning" | "invoice" | "comment" | "reorder";

function ActionButton({
  onClick,
  text,
  styleType,
}: {
  onClick: () => void;
  text: string;
  styleType: ButtonStyle;
}) {
  const styles: Record<ButtonStyle, React.CSSProperties> = {
    primary: { background: "#FF6B35", color: "#fff" },
    "danger-outline": { border: "1px solid #EF4444", color: "#EF4444", background: "#FFF" },
    info: { background: "#6366F1", color: "#fff" },
    warning: { border: "1px solid #F59E0B", color: "#F59E0B", background: "#FFFBEB" },
    invoice: { border: "1px solid #2563EB", color: "#2563EB", background: "#EFF6FF" },
    comment: { background: "#F59E0B", color: "#fff" },
    reorder: { background: "#10B981", color: "#fff" },
  };

  const isTextWhite = styleType === "primary" || styleType === "info" || styleType === "comment" || styleType === "reorder";

  return (
    <button
      onClick={onClick}
      className={`flex-1 py-2.5 rounded-full text-sm font-medium cursor-pointer ${isTextWhite ? "text-white" : ""}`}
      style={styles[styleType]}
    >
      {text}
    </button>
  );
}
